import { useEffect, useState } from "react";
import { SvgClose } from "ui/svgs";
import { Popover as PopoverAntd } from "antd";
import { Popover } from "react-tiny-popover";
import { toPascalCase } from "lib/utils";
import clsx from "clsx";
import { USER_ROLE } from "lib/constants";

type RoleSelectPopoverProps = {
  availableRoles: string[];
  selectedRoles: string[];
  onRolesChange: (roles: string[]) => void;
  multiSelect?: boolean;
};

export default function RoleSelectPopover({
  selectedRoles,
  onRolesChange,
  multiSelect = true,
  availableRoles,
}: RoleSelectPopoverProps) {
  const [isRolePopoverOpen, setIsRolesPopoverOpen] = useState(false);

  const [stagedRoles, setStagedRoles] = useState([]);

  useEffect(() => {
    if (isRolePopoverOpen) {
      setStagedRoles(selectedRoles); // Set staged roles to the currently selected ones when popover opens
    }
  }, [isRolePopoverOpen]);

  const handleRoleSelection = (role) => {
    setStagedRoles((prevStagedRoles) =>
      prevStagedRoles.includes(role)
        ? prevStagedRoles.filter((role) => role !== role)
        : multiSelect
          ? [...prevStagedRoles, role]
          : [role],
    );
  };

  const handleRoleCancel = () => {
    setIsRolesPopoverOpen(false);
  };

  const handleRoleOk = () => {
    setIsRolesPopoverOpen(false);

    onRolesChange?.(stagedRoles);
  };
  const clearRoles = (e) => {
    e.stopPropagation();
    setStagedRoles([]);
    onRolesChange?.([]);
  };

  const renderRolesDisplay = () => {
    const count = selectedRoles?.length;
    if (count === 0) return "Role";
    if (count === 1)
      return (
        availableRoles?.find((role) => role === selectedRoles?.[0]) || "1 Role"
      );
    if (count === availableRoles?.length) return "All Roles";
    return `${availableRoles?.find((role) => role === selectedRoles?.[0])} (${count})`;
  };

  return (
    <PopoverAntd
      open={isRolePopoverOpen}
      arrow={false}
      trigger="click"
      rootClassName="noPaddingPopover"
      onOpenChange={setIsRolesPopoverOpen}
      content={
        <div className="border rounded-xl shadow-xl bg-white min-w-44 pt-2 px-2 text-[#222222]">
          <div className="max-h-64 overflow-y-auto">
            {availableRoles?.map((role) => (
              <button
                key={role}
                className={clsx(
                  "block w-full  p-2",
                  stagedRoles.includes(role)
                    ? "bg-blue-100"
                    : "hover:bg-neutral-100",
                )}
                onClick={() => handleRoleSelection(role)}
              >
                <p className="text-sm text-left">
                  {toPascalCase(role as USER_ROLE)}
                </p>
              </button>
            ))}
          </div>
          <div className="flex justify-end space-x-2 p-2">
            <button
              className="px-4 py-1 rounded text-sm text-blue-700 hover:text-blue-500"
              onClick={handleRoleCancel}
            >
              Cancel
            </button>
            <button
              className="px-4 py-1 rounded text-sm bg-blue-600 hover:bg-blue-500 text-white"
              onClick={handleRoleOk}
            >
              OK
            </button>
          </div>
        </div>
      }
    >
      {selectedRoles?.length > 0 ? (
        <button
          className="flex items-center rounded-xl bg-blue-100 border px-3 py-2 w-44 h-[33px] overflow-hidden"
          onClick={() => setIsRolesPopoverOpen(!isRolePopoverOpen)}
        >
          <p className="text-sm font-semibold text-left flex-grow pr-2 max-w-[140px]  pl-2 text-nowrap overflow-hidden break-words text-ellipsis">
            {renderRolesDisplay()}
          </p>
          <button onClick={clearRoles}>
            <SvgClose
              width="13"
              className="fill-neutral-800 hover:fill-neutral-700"
            />
          </button>
        </button>
      ) : (
        <button
          className="flex items-center rounded-xl hover:bg-zinc-200 border border-neutral-400 px-3 py-2 min-w-[76px] h-[33px]"
          onClick={() => setIsRolesPopoverOpen(!isRolePopoverOpen)}
        >
          <p className="text-sm font-semibold text-left  pl-2 text-nowrap">
            Role
          </p>
        </button>
      )}
    </PopoverAntd>
  );
}
